import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCitiesByProvider, ensureCitiesLoaded } from '../services/citiesService';
import { getDeliveryCompanies, getProductVariants } from '../services/api';
import TrackingTimeline from './TrackingTimeline';
import CityCombobox from './CityCombobox';
import {
    X,
    User,
    Phone,
    MapPin,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Copy,
    MessageCircle,
    Package,
    Edit3,
    DollarSign,
    Clock,
    PhoneOff,
    Calendar,
    Eye,
    AlertTriangle,
    PhoneForwarded,
    MessageSquare,
    UserX,
    Mic,
    ThumbsDown,
    Ghost
} from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { SIZE_EXPANSION, normalizeSize, formatSize } from '../utils/variantUtils';


const StatusSelector = ({ currentStatus, onStatusChange }) => {
    const { t } = useTranslation();
    const statuses = [
        { id: 'confirmed', label: t('status.order.confirmed'), icon: CheckCircle2, color: 'bg-green-50 text-green-700 border-green-100' },
        { id: 'appel_1', label: t('status.order.appel_1'), icon: Phone, color: 'bg-amber-50 text-amber-700 border-amber-100' },
        { id: 'appel_2', label: t('status.order.appel_2'), icon: PhoneForwarded, color: 'bg-amber-50 text-amber-700 border-amber-100' },
        { id: 'appel_3', label: t('status.order.appel_3'), icon: PhoneForwarded, color: 'bg-amber-50 text-amber-700 border-amber-100' },
        { id: 'call_whatsapp', label: t('status.order.call_whatsapp'), icon: MessageCircle, color: 'bg-green-50 text-green-700 border-green-100' },
        { id: 'call_sms', label: t('status.order.call_sms'), icon: MessageSquare, color: 'bg-indigo-50 text-indigo-700 border-indigo-100' },
        { id: 'no_answer', label: t('status.order.no_answer'), icon: PhoneOff, color: 'bg-gray-100 text-gray-600 border-gray-200' },
        { id: 'postponed', label: t('status.order.postponed'), icon: Calendar, color: 'bg-blue-50 text-blue-700 border-blue-100' },
        { id: 'unreachable', label: t('status.order.unreachable'), icon: UserX, color: 'bg-red-50 text-red-700 border-red-100' },
        { id: 'voicemail', label: t('status.order.voicemail'), icon: Mic, color: 'bg-teal-50 text-teal-700 border-teal-100' },
        { id: 'cancelled', label: t('status.order.cancelled'), icon: X, color: 'bg-red-50 text-red-700 border-red-100' },
        { id: 'not_serious', label: t('status.order.not_serious'), icon: ThumbsDown, color: 'bg-rose-50 text-rose-700 border-rose-100' },
        { id: 'fake_order', label: t('status.order.fake_order'), icon: Ghost, color: 'bg-slate-50 text-slate-700 border-slate-100' },
        { id: 'double_order', label: t('status.order.double_order'), icon: Copy, color: 'bg-neutral-50 text-neutral-700 border-neutral-100' },
    ];

    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {statuses.map((s) => (
                <button
                    key={s.id}
                    type="button"
                    onClick={() => onStatusChange(s.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-[11px] font-bold transition-all ${currentStatus === s.id
                        ? `${s.color} border-current ring-1 ring-current`
                        : 'bg-white text-gray-400 border-gray-100 hover:border-gray-300'
                        }`}
                >
                    <s.icon size={14} />
                    {s.label}
                </button>
            ))}
        </div>
    );
};

const ConfirmationModal = ({ order, isOpen, onClose, onSave, readOnly = false }) => {
    const { t, i18n } = useTranslation();
    const [providers, setProviders] = useState([]);
    const [loadingProviders, setLoadingProviders] = useState(false);
    const [status, setStatus] = useState('pending');
    const [formData, setFormData] = useState({
        receiverName: '',
        phone: '',
        providerSlug: '', // Changed from 'ozonexpress'
        cityId: '',
        cityName: '',
        address: '',
        price: 0,
        variant: { color: '', size: '' }, // Structured
        note: ''
    });
    const [availableVariants, setAvailableVariants] = useState([]);
    const [loadingVariants, setLoadingVariants] = useState(false);
    const [cities, setCities] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [toasts, setToasts] = useState([]);

    const showToast = (type, message) => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, type, message }]);
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 3000);
    };

    const resetForm = React.useCallback((availableProviders) => {
        if (order) {
            // Priority: Order slug -> Brand Default (if we had it) -> first available -> empty
            let providerSlug = order.shipping?.providerSlug || '';

            // If order has no slug, try to pick the default or first from availableProviders
            if (!providerSlug) {
                providerSlug = '';
            }

            const cityId = order.shipping?.cityId || '';
            const cities = getCitiesByProvider(providerSlug);
            const selectedCity = cities.find(c => String(c.id) === String(cityId));
            const cityName = selectedCity?.name || ''; // Validated name only, no order.city fallback

            setFormData({
                receiverName: order.shipping?.receiverName || order.name || '',
                phone: order.shipping?.phone || order.phone || '',
                providerSlug,
                cityId,
                cityName,
                address: order.shipping?.address || order.address || '',
                price: order.shipping?.price || order.price || 0,
                variant: {
                    color: (typeof order.shipping?.variant === 'object' ? order.shipping.variant.color : '') || (typeof order.variant === 'object' ? order.variant.color : '') || '',
                    size: (typeof order.shipping?.variant === 'object' ? order.shipping.variant.size : '') || (typeof order.variant === 'object' ? order.variant.size : '') || ''
                },
                note: order.shipping?.note || order.notes || ''
            });

            // Fetch live variants if we have a product
            if (order.productId?._id || order.productId) {
                const pid = order.productId?._id || order.productId;
                setLoadingVariants(true);
                getProductVariants(pid)
                    .then(res => {
                        const variantList = res.data || [];
                        setAvailableVariants(variantList);
                        setLoadingVariants(false);

                        // AUTO-MATCHING LOGIC REMOVED AS PER USER REQUEST (Manual selection only)
                    })
                    .catch(err => {
                        console.error("[ConfirmationModal] Failed to fetch variants:", err);
                        setLoadingVariants(false);
                    });
            }

            // Pre-load cities for the initial provider
            if (providerSlug) {
                setLoadingCities(true);
                ensureCitiesLoaded(providerSlug).then(fetched => {
                    setCities(fetched);
                    setLoadingCities(false);
                });
            }

            setStatus(order.status === 'confirmed_ready_for_shipping' ? 'confirmed' : (order.status || 'pending'));
            setError(null);
        }
    }, [order]);

    useEffect(() => {
        const fetchProviders = async () => {
            setLoadingProviders(true);
            try {
                const res = await getDeliveryCompanies();
                if (res.success) {
                    // Only show connected ones
                    const connected = res.data
                        .filter(p => p.isConnected)
                        .map(p => ({ slug: p.slug, name: p.name, isDefault: p.isDefault }));
                    setProviders(connected);
                    // Pass current providers to resetForm to handle the initial slug correctly
                    resetForm(connected);
                }
            } catch (err) {
                console.error("Error fetching providers:", err);
                setProviders([]); // Set to empty array if error
            } finally {
                setLoadingProviders(false);
            }
        };

        if (isOpen) {
            fetchProviders();
        }
    }, [isOpen, resetForm]);

    useEffect(() => {
        if (isOpen && providers.length > 0) { // Ensure providers are loaded before resetting
            resetForm(providers);
        } else if (isOpen && providers.length === 0 && !loadingProviders) {
            // If no providers are found after loading, reset with empty providers
            resetForm([]);
        }
    }, [order, isOpen, providers, loadingProviders, resetForm]); // Added providers and loadingProviders to dependencies

    useEffect(() => {
        if (formData.providerSlug) {
            setLoadingCities(true);
            ensureCitiesLoaded(formData.providerSlug).then(fetched => {
                setCities(fetched);
                setLoadingCities(false);
            });
        } else {
            setCities([]);
        }
    }, [formData.providerSlug]);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen || !order) return null;

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    const handleWhatsApp = () => {
        const phone = formData.phone.replace(/\s/g, '');
        window.open(`https://wa.me/${phone}`, '_blank');
    };

 

    const handleCitySelect = (id, name) => {
        setFormData(prev => ({ ...prev, cityId: id, cityName: name }));
        if (id) setError(null);
    };

    const handleSave = async () => {
        if (status === 'pending') {
            showToast("error", t('confirmation_modal.messages.status_required'));
            return;
        }

        if (status === 'confirmed') {
            const required = [
                { field: 'receiverName', label: t('confirmation_modal.fields.receiver_name') },
                { field: 'phone', label: t('confirmation_modal.fields.phone') },
                { field: 'providerSlug', label: t('confirmation_modal.fields.delivery_company') },
                { field: 'cityId', label: t('confirmation_modal.fields.validated_city') },
                { field: 'cityName', label: t('confirmation_modal.fields.validated_city') },
                { field: 'address', label: t('confirmation_modal.fields.full_address') },
                { field: 'price', label: t('confirmation_modal.fields.amount') }
            ];
            const missing = required.filter(item => !formData[item.field]);
            
            // Validate variant selection (color & size) for confirmed orders
            if (!formData.variant.color || !formData.variant.size) {
                setError("La couleur et la taille sont obligatoires pour confirmer la commande.");
                return;
            }

            if (missing.length > 0) {
                setError(t('confirmation_modal.messages.field_required', { field: missing[0].label }));
                return;
            }

            // Stock check for confirmation
            const selectedVariant = availableVariants.find(v => 
                v.color === formData.variant.color && v.size === formData.variant.size
            );
            if (selectedVariant && selectedVariant.quantity <= 0) {
                setError("Rupture de stock pour cette variante. Impossible de confirmer.");
                return;
            }
        }

        // Consistency check: Ensure cityName matches cityId before saving
        const cityMatch = cities.find(c => String(c.id) === String(formData.cityId));
        const finalShippingData = {
            ...formData,
            cityName: cityMatch?.name || formData.cityName
        };

        setIsSaving(true);
        setError(null);
        try {
            await onSave(order._id, { 
                shipping: finalShippingData, 
                status, 
                note: formData.note 
            });
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-sm">
                <div className="bg-white rounded-[2rem] w-full max-w-5xl shadow-2xl overflow-hidden flex flex-col md:flex-row max-h-[85vh]">

                    {/* Left Side: Order Info (Compact) */}
                    <div className="bg-gray-50/50 p-4 md:w-80 border-r border-gray-100 overflow-y-auto hidden md:block scrollbar-thin">
                        <div className="mb-4">
                            <h2 className="text-base font-bold text-gray-900 tracking-tight">{t('confirmation_modal.sections.details')}</h2>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mt-0.5">#{order.orderId}</p>
                        </div>

                        <div className="space-y-3">
                            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                                <p className="text-[9px] font-semibold text-gray-300 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                                    <Package size={10} /> {t('confirmation_modal.sections.product')}
                                </p>
                                {/* Product Image */}
                                {order.productImage && (
                                    <div className="mb-2 rounded-lg overflow-hidden border border-gray-100">
                                        <img
                                            src={order.productImage}
                                            alt={order.productName}
                                            className="w-full h-48 object-cover"
                                            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                        />
                                    </div>
                                )}
                                <h3 className="font-semibold text-gray-800 text-xs leading-snug mb-1">{order.productName}</h3>
                                <div className="flex justify-between items-center text-[11px]">
                                    <span className="text-gray-400 font-medium">{formatSize(order.variant) || 'Standard'}</span>
                                    <span className="font-bold text-indigo-500">x{order.quantity}</span>
                                </div>
                            </div>

                            <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                                <p className="text-[9px] font-semibold text-gray-300 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                                    <DollarSign size={10} /> {t('confirmation_modal.sections.summary')}
                                </p>
                                <div className="space-y-1">
                                    <div className="flex justify-between text-[11px] font-medium">
                                        <span className="text-gray-400">{t('confirmation_modal.fields.price')}</span>
                                        <span className="text-gray-700">{order.price?.toLocaleString()} {t('shipping.order_card.amount_currency')}</span>
                                    </div>
                                    <div className="flex justify-between text-xs font-bold pt-1.5 border-t border-gray-50">
                                        <span className="text-gray-800">{t('confirmation_modal.fields.total')}</span>
                                        <span className="text-indigo-600">{(order.price || 0).toLocaleString()} {t('shipping.order_card.amount_currency')}</span>
                                    </div>
                                </div>
                            </div>

                            {order.utmSource && (
                                <div className="bg-indigo-50/30 p-3 rounded-xl border border-indigo-100/50">
                                    <p className="text-[9px] font-semibold text-indigo-300 uppercase tracking-widest mb-0.5">{t('confirmation_modal.sections.source')}</p>
                                    <p className="text-xs font-semibold text-indigo-600">{order.utmSource}</p>
                                </div>
                            )}

                            {order.shipping?.trackingNumber && (
                                <div className="bg-green-50/30 p-3 rounded-xl border border-green-100/50">
                                    <p className="text-[9px] font-semibold text-green-400 uppercase tracking-widest mb-0.5">{t('confirmation_modal.sections.tracking_generated')}</p>
                                    <p className="text-xs font-bold text-green-600 break-all">{order.shipping.trackingNumber}</p>
                                </div>
                            )}

                            {(order.delivery?.statusHistory?.length > 0 || order.delivery?.status) && (
                                <div className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm">
                                    <h4 className="text-[9px] font-semibold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                                        <Clock size={10} className="text-indigo-500" /> {t('confirmation_modal.sections.tracking_history')}
                                    </h4>
                                    <TrackingTimeline
                                        history={order.delivery?.statusHistory}
                                        currentStatus={order.delivery?.status}
                                    />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Side: Compact Form */}
                    <div className="flex-1 flex flex-col bg-white overflow-hidden relative">
                        {/* Sticky Header */}
                        <div className="px-6 py-4 border-b border-gray-50 flex justify-between items-center sticky top-0 bg-white z-20">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600">
                                    {readOnly ? <Eye size={16} /> : <Edit3 size={16} />}
                                </div>
                                <h2 className="text-base font-black text-gray-900">{readOnly ? t('confirmation_modal.view_title') : t('confirmation_modal.edit_title')}</h2>
                            </div>
                            <button
                                onClick={handleClose}
                                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <X size={20} className="text-gray-300" />
                            </button>
                        </div>

                        {/* Scrollable Body */}
                        <div className="p-6 flex-1 overflow-y-auto scrollbar-thin">
                            {/* Status Selection at the top for visibility */}
                            {!readOnly && (
                                <div className="mb-6">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 block">{t('confirmation_modal.fields.status')}</label>
                                    <StatusSelector currentStatus={status} onStatusChange={setStatus} />
                                </div>
                            )}

                            {order.isPotentialDuplicate && (
                                <div className="mb-6 bg-red-50 border border-red-100 rounded-2xl p-4 flex items-start gap-3 animate-pulse">
                                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600 flex-shrink-0 mt-0.5">
                                        <AlertTriangle size={20} />
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="text-red-700 font-black text-sm uppercase tracking-tight mb-1">{t('orders.messages.duplicate_warning_title')}</h4>
                                        <p className="text-red-600/80 text-xs font-medium leading-relaxed mb-2">
                                            {t('orders.messages.duplicate_warning_subtitle')}
                                        </p>
                                        <div className="bg-white/50 rounded-xl p-3 border border-red-100/50 text-[11px] space-y-1.5">
                                            <p className="text-gray-500 font-bold uppercase tracking-widest text-[9px] mb-1">{t('orders.messages.matched_order_details')}</p>
                                            <div className="flex justify-between">
                                                <span className="text-gray-400 font-bold uppercase tracking-widest text-[9px]">{t('orders.messages.sheet_date')}:</span>
                                                <span className="font-bold text-gray-700">{formatDate(order.potentialDuplicateOf?.orderDate, i18n.language)}</span>
                                            </div>

                                            <div className="pt-1.5 mt-1.5 border-t border-red-100/30 space-y-1">
                                                <div className="flex justify-between">
                                                    <span className="text-gray-400">ID:</span>
                                                    <span className="font-bold text-gray-700">{order.potentialDuplicateOf?.orderId ? `#${order.potentialDuplicateOf.orderId}` : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-400">Status:</span>
                                                    <span className="font-bold text-gray-700">{order.potentialDuplicateOf?.status ? t(`status.order.${order.potentialDuplicateOf.status}`) : 'N/A'}</span>
                                                </div>
                                                <div className="flex justify-between">
                                                    <span className="text-gray-400">Assigned to:</span>
                                                    <span className="font-bold text-gray-700">{order.potentialDuplicateOf?.assignedTo?.name || t('orders.table.unassigned')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="sm:col-span-2">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5 block">{t('confirmation_modal.fields.delivery_company')}</label>
                                    <select
                                        disabled={readOnly || providers.length === 0}
                                        className={`w-full px-4 py-2.5 rounded-xl bg-gray-50 border ${providers.length === 0 && status === 'confirmed' ? 'border-red-500 ring-2 ring-red-500/5' : 'border-gray-100'} focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm text-gray-800 transition-all appearance-none`}
                                        value={formData.providerSlug}
                                        onChange={(e) => setFormData({ ...formData, providerSlug: e.target.value, cityId: '', cityName: '' })}
                                    >
                                        {providers.length > 0 ? (
                                            <>
                                                <option value="">{t('confirmation_modal.placeholders.select_provider')}</option>
                                                {providers.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                                            </>
                                        ) : (
                                            <option value="">{t('confirmation_modal.placeholders.no_providers')}</option>
                                        )}
                                    </select>
                                </div>

                                <form autoComplete="off" className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" onSubmit={(e) => e.preventDefault()}>
                                    <div className="sm:col-span-2 bg-green-50/50 rounded-2xl p-4 border border-green-100 flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-green-600 rounded-xl flex items-center justify-center text-white shadow-sm">
                                                <Phone size={20} />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-black text-green-700 tracking-tight">{order.phone}</h3>
                                                <p className="text-[9px] font-bold text-green-600/60 uppercase tracking-widest">{t('confirmation_modal.fields.phone')}</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleCopy(order.phone)}
                                                className="p-2 bg-white hover:bg-green-100 text-green-600 rounded-xl transition-all border border-green-100"
                                            >
                                                <Copy size={16} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleWhatsApp}
                                                className="p-2 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all shadow-sm"
                                            >
                                                <MessageCircle size={16} />
                                            </button>
                                        </div>
                                    </div>

                                    <div className="sm:col-span-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5 block">
                                            {t('confirmation_modal.fields.receiver_name')} {status === 'confirmed' && <span className="text-red-500">*</span>}
                                        </label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                                            <input
                                                type="text"
                                                name="x-receiver-fn"
                                                disabled={readOnly}
                                                className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${error && !formData.receiverName && status === 'confirmed' ? 'border-red-500 ring-2 ring-red-500/5' : 'border-gray-100'} focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm text-gray-800 transition-all ${readOnly ? 'cursor-default' : ''}`}
                                                placeholder={t('confirmation_modal.placeholders.receiver_name')}
                                                value={formData.receiverName}
                                                onChange={(e) => setFormData({ ...formData, receiverName: e.target.value })}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5 block">
                                            {t('confirmation_modal.fields.phone')} {status === 'confirmed' && <span className="text-red-500">*</span>}
                                        </label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                                            <input
                                                type="text"
                                                name="x-phone-num"
                                                disabled={readOnly}
                                                className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${error && !formData.phone && status === 'confirmed' ? 'border-red-500 ring-2 ring-red-500/5' : 'border-gray-100'} focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm text-gray-800 transition-all ${readOnly ? 'cursor-default' : ''}`}
                                                placeholder={t('confirmation_modal.placeholders.phone')}
                                                value={formData.phone}
                                                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5 block">
                                            {t('confirmation_modal.fields.amount')} {status === 'confirmed' && <span className="text-red-500">*</span>}
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                                            <input
                                                type="number"
                                                disabled={readOnly}
                                                className={`w-full pl-9 pr-3 py-2.5 rounded-xl bg-gray-50 border ${error && !formData.price && status === 'confirmed' ? 'border-red-500 ring-2 ring-red-500/5' : 'border-gray-100'} focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm text-gray-800 transition-all ${readOnly ? 'cursor-default' : ''}`}
                                                value={formData.price}
                                                onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>



                                    <div className="sm:col-span-2 space-y-4 bg-indigo-50/20 p-4 rounded-2xl border border-indigo-100/50">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                                <Package size={12} className="text-indigo-500" />
                                                SÉLECTION DE LA VARIANTE
                                            </label>
                                            {loadingVariants && <Loader2 size={12} className="animate-spin text-indigo-400" />}
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            {/* Color Select */}
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">
                                                    Couleur {availableVariants.length > 0 && `(${[...new Set(availableVariants.map(v => v.color))].length})`}
                                                </p>
                                                <select
                                                    disabled={readOnly}
                                                    className={`w-full px-3 py-2.5 rounded-xl border focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm text-gray-800 transition-all appearance-none ${availableVariants.length === 0 ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' : 'bg-white border-gray-100'}`}
                                                    value={formData.variant.color}
                                                    onChange={(e) => setFormData({ 
                                                        ...formData, 
                                                        variant: { color: e.target.value, size: '' } 
                                                    })}
                                                >
                                                    {availableVariants.length === 0 ? (
                                                        <option value="">{loadingVariants ? 'Chargement...' : 'Aucune variante trouvée'}</option>
                                                    ) : (
                                                        <>
                                                            <option value="">Sélectionner</option>
                                                            {[...new Set(availableVariants.map(v => v.color))].map(c => (
                                                                <option key={c} value={c}>{c}</option>
                                                            ))}
                                                        </>
                                                    )}
                                                </select>
                                            </div>

                                            {/* Size Select */}
                                            <div>
                                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">Taille</p>
                                                <select
                                                    disabled={readOnly || !formData.variant.color}
                                                    className="w-full px-3 py-2.5 rounded-xl bg-white border border-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm text-gray-800 transition-all appearance-none"
                                                    value={formData.variant.size}
                                                    onChange={(e) => setFormData({ 
                                                        ...formData, 
                                                        variant: { ...formData.variant, size: e.target.value } 
                                                    })}
                                                >
                                                    <option value="">Sélectionner</option>
                                                    {availableVariants
                                                        .filter(v => v.color === formData.variant.color)
                                                        .flatMap(v => {
                                                            const expanded = SIZE_EXPANSION[v.size];
                                                            if (expanded) {
                                                                return expanded.map(label => ({
                                                                    label,
                                                                    value: label,
                                                                    quantity: v.quantity
                                                                }));
                                                            }
                                                            return [{ label: v.size, value: v.size, quantity: v.quantity }];
                                                        })
                                                        .map((opt, idx) => (
                                                            <option key={`${opt.value}-${idx}`} value={opt.value} disabled={opt.quantity <= 0}>
                                                                {opt.label} {opt.quantity <= 0 ? '(Rupture)' : `(${opt.quantity})`}
                                                            </option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                        </div>

                                        {/* Stock Badge */}
                                        {formData.variant.color && formData.variant.size && (
                                            <div className="flex items-center gap-2 pt-1 animate-in fade-in zoom-in-95">
                                                {(() => {
                                                    const normalizedSizeSearch = normalizeSize(formData.variant.size);
                                                    const v = availableVariants.find(x => x.color === formData.variant.color && x.size === normalizedSizeSearch);
                                                    if (!v) return null;
                                                    const colorClass = v.quantity <= 0 ? 'bg-red-50 text-red-600 border-red-100' : 
                                                                     v.quantity <= 5 ? 'bg-amber-50 text-amber-600 border-amber-100' : 
                                                                     'bg-emerald-50 text-emerald-600 border-emerald-100';
                                                    return (
                                                        <div className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${colorClass}`}>
                                                            {v.quantity <= 0 ? '🔴 Rupture' : v.quantity <= 5 ? `🟡 Stock Faible: ${v.quantity}` : `🟢 En Stock: ${v.quantity}`}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        )}

                                        <p className="text-[9px] text-gray-400 mt-1 ml-1 font-medium italic">
                                            Variante Sheet : <bdi className="font-bold text-gray-600">
                                                {order.sheetVariant || (typeof order.variant === 'string' ? order.variant : 'Aucune')}
                                            </bdi>
                                        </p>
                                    </div>
                                    <div className="sm:col-span-2 space-y-1.5">
                                        <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
                                            {/* Original City from Order */}
                                            <div className="sm:w-32 flex-none group">
                                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-1 group-hover:text-gray-500 transition-colors">
                                                    {t('confirmation_modal.fields.from_order')}
                                                </p>
                                                <div className="h-[42px] flex items-center px-3 rounded-xl bg-gray-50/50 border border-gray-100 text-gray-400 font-bold text-xs uppercase tracking-tight whitespace-nowrap overflow-hidden shadow-sm transition-all hover:bg-gray-50">
                                                    <MapPin size={12} className="mr-1.5 opacity-50 shrink-0" />
                                                    <span className="truncate">
                                                        <span className="text-gray-700 font-black">{order.city || t('sidebar.status.none')}</span>
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Validated City Dropdown */}
                                            <div className="flex-1">
                                                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5 block flex items-center gap-1.5">
                                                    {t('confirmation_modal.fields.validated_city')} {status === 'confirmed' && <span className="text-red-500">*</span>}
                                                </label>
                                                <div className="relative">
                                                    <CityCombobox
                                                        cities={cities}
                                                        value={formData.cityId}
                                                        onChange={handleCitySelect}
                                                        placeholder={t('confirmation_modal.placeholders.select_city')}
                                                        disabled={readOnly}
                                                        isLoading={loadingCities}
                                                        hasError={!!(error && !formData.cityId && status === 'confirmed')}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="sm:col-span-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5 block">
                                            {t('confirmation_modal.fields.full_address')} {status === 'confirmed' && <span className="text-red-500">*</span>}
                                        </label>
                                        <div className="relative">
                                            <textarea
                                                rows="2"
                                                name="x-ship-addr"
                                                disabled={readOnly}
                                                className={`w-full px-3 py-2.5 rounded-xl bg-gray-50 border ${error && !formData.address && status === 'confirmed' ? 'border-red-500 ring-2 ring-red-500/5' : 'border-gray-100'} focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-sm text-gray-800 transition-all resize-none ${readOnly ? 'cursor-default' : ''}`}
                                                placeholder={t('confirmation_modal.placeholders.address')}
                                                value={formData.address}
                                                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                                autoComplete="new-password"
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="sm:col-span-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1.5 ml-0.5 block">{t('confirmation_modal.fields.note')}</label>
                                        <textarea
                                            rows="4"
                                            name="x-order-note"
                                            disabled={readOnly}
                                            className={`w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-xs text-gray-600 transition-all resize-none ${readOnly ? 'cursor-default' : ''}`}
                                            placeholder={t('confirmation_modal.placeholders.note')}
                                            value={formData.note}
                                            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                                            autoComplete="new-password"
                                        ></textarea>
                                    </div>
                                </form>
                            </div>

                            {/* Sticky Footer (Compact) */}
                            <div className="px-6 py-4 border-t border-gray-50 flex items-center justify-between bg-gray-50/30 sticky bottom-0 z-20">
                                <div className="flex-1 mr-4">
                                    {error && (
                                        <div className="flex items-center gap-1.5 text-red-500 animate-in fade-in slide-in-from-bottom-1">
                                            <AlertCircle size={14} />
                                            <p className="text-[10px] font-black uppercase tracking-widest">{error}</p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleClose}
                                        className="px-4 py-2.5 rounded-xl font-bold text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest text-[10px]"
                                    >
                                        {readOnly ? t('confirmation_modal.actions.close') : t('confirmation_modal.actions.cancel')}
                                    </button>
                                    {!readOnly && (
                                        <button
                                            onClick={handleSave}
                                            disabled={isSaving}
                                            className="px-6 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl font-black transition-all shadow-lg shadow-gray-200 flex items-center gap-2 uppercase tracking-widest text-[10px] disabled:opacity-50"
                                        >
                                            {isSaving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
                                            <span>{t('confirmation_modal.actions.save')}</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div >
                    </div >

                    {/* Toasts */}
                    < div className="fixed top-6 right-6 z-[110] flex flex-col gap-3 pointer-events-none" >
                        {
                            toasts.map(toast => (
                                <div
                                    key={toast.id}
                                    className={`flex items-center gap-3 px-6 py-4 rounded-3xl shadow-2xl border animate-in slide-in-from-right-full duration-500 pointer-events-auto ${toast.type === 'success'
                                        ? 'bg-white border-green-100 text-gray-900'
                                        : 'bg-white border-red-100 text-gray-900'
                                        }`}
                                >
                                    {toast.type === 'success' ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : (
                                        <AlertCircle className="w-5 h-5 text-red-500" />
                                    )}
                                    <span className="font-bold text-sm tracking-tight">{toast.message}</span>
                                </div>
                            ))
                        }
                    </div>
                </div>
            </div>
        </>
    );
};

export default ConfirmationModal;
